import {Component, ElementRef, NgZone, OnInit, ViewChild} from '@angular/core';
import {UserModel} from '../../../models/authentication/user.model';
import {ActivatedRoute, Router} from '@angular/router';
import {BookService} from '../../../services/public/book.service';
import {ModalService} from '../../../shared/modals/modal/modal.service';
import {BookModel} from '../../../models/edu-admin/book.model';
import {BookVersionModel} from '../../../models/edu-admin/book-version.model';
import {CountryModel} from '../../../models/localization-and-pricing/country.model';
import {StudentModel} from '../../../models/authentication/student.model';
import {TranslateService} from '@ngx-translate/core';
import {PricingModel, SubscriptionPricingModel} from '../../../models/localization-and-pricing/pricing.model';
import {AdminPricingService} from '../../../services/admin/admin-pricing.service';
import {ChargebeeService} from '../../../services/student/chargebee.service';
import {NavigationModel} from '../../../models/localization-and-pricing/navigation.model';
import {ShippingInfoModel} from './shipping-info/shipping-info.component';
import {DroppService} from '../../../services/integrations/dropp.service';

@Component({
  selector: 'app-billing-ov',
  templateUrl: './billing-ov.component.html',
  styleUrls: ['./billing-ov.component.css']
})
export class BillingOvComponent implements OnInit {

  acceptTCs = false;

  bookId: string;
  bookVersionId: string;
  subscriptionType: string;

  book: BookModel;
  bookVersion: BookVersionModel;

  pricing: PricingModel;
  subPricing: SubscriptionPricingModel;

  cbInstance: any;

  chargebeeShippingInfo = {
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: ''
  };

  shippingInfo: ShippingInfoModel = new ShippingInfoModel();

  constructor(private route: ActivatedRoute,
              private router: Router,
              private droppSv: DroppService,
              private bookSv: BookService,
              private pricingAdminSv: AdminPricingService,
              private modalSv: ModalService,
              private cbSv: ChargebeeService,
              private tSv: TranslateService,
              private ngZone: NgZone) { }

  ngOnInit() {

    if (window['Chargebee']) {
      this.cbInstance = window['Chargebee'].getInstance();
    }

    this.route.queryParams.subscribe((params) => {

      this.subscriptionType = params['subscription'];
      this.bookId = params['bookId'];
      this.bookVersionId = params['bookVersionId'];

      if (this.subscriptionType === null || this.subscriptionType === undefined) {

        this.router.navigate(NavigationModel.getHomeURLPaths(['landing']));

      }

      if (this.bookId && this.bookVersionId) {

        this.bookSv.fetchPublicBook(this.bookId).subscribe((book) => {

          this.book = book;
          this.bookVersion = book.findVersion(this.bookVersionId);

        });

      }

      this.pricingAdminSv.fetchPricing(UserModel.getCurrent().country).subscribe((pricing) => {

        this.pricing = pricing;
        this.subPricing = pricing[this.subscriptionType];

      });


    });

  }

  setShippingLocation(shipping: ShippingInfoModel) {

    this.shippingInfo = shipping;

  }

  showShippingOptions(): boolean {

    if (this.subPricing &&
      this.subPricing.includesBook &&
      UserModel.getCurrent().country === CountryModel.ISL) {

      return true;
    }

    return false;
  }

  getTrialDuration(): string {

    if (this.pricing) {
      return this.pricing.trial.trialLength.toString(10);
    }

    return '0';
  }

  getTrialCost(): string {

    if (!this.pricing) {
      return '0';
    }

    return '0 ' + this.pricing['1M'].currencySymbol;
  }

  openCheckout() {

    const errTitle = this.tSv.instant('reusable.error');

    // Throw error if terms are not accepted.
    if (this.acceptTCs === false) {

      const errMessage = this.tSv.instant('checkout.termsNotAcceptedError');
      return this.modalSv.showErrorModal(errTitle, errMessage);

    }

    if (this.subPricing.includesBook === true &&
      this.shippingInfo &&
      UserModel.getCurrent().country === CountryModel.ISL) {

      const missingFieldError = this.tSv.instant('checkout.missingFieldError');

      if (!this.shippingInfo.firstName) {

        const fieldName = this.tSv.instant('fieldNames.firstName');
        const errMessage = `${fieldName} ${missingFieldError}`;

        return this.modalSv.showErrorModal(errTitle, errMessage);

      } else if (!this.shippingInfo.lastName) {

        const fieldName = this.tSv.instant('fieldNames.lastName');
        const errMessage = `${fieldName} ${missingFieldError}`;

        return this.modalSv.showErrorModal(errTitle, errMessage);

      } else if (!this.shippingInfo.phone) {

        const fieldName = this.tSv.instant('fieldNames.phone');
        const errMessage = `${fieldName} ${missingFieldError}`;

        return this.modalSv.showErrorModal(errTitle, errMessage);

      } else if (!this.shippingInfo.address) {

        const fieldName = this.tSv.instant('fieldNames.address');
        const errMessage = `${fieldName} ${missingFieldError}`;

        return this.modalSv.showErrorModal(errTitle, errMessage);

      } else if (!this.shippingInfo.zip) {

        const fieldName = this.tSv.instant('fieldNames.zip');
        const errMessage = `${fieldName} ${missingFieldError}`;

        return this.modalSv.showErrorModal(errTitle, errMessage);

      } else if (!this.shippingInfo.city) {

        const fieldName = this.tSv.instant('fieldNames.city');
        const errMessage = `${fieldName} ${missingFieldError}`;

        return this.modalSv.showErrorModal(errTitle, errMessage);

      } else if (!this.shippingInfo.location) {

        const errMessage = this.tSv.instant('checkout.shippingLocationError');

        return this.modalSv.showErrorModal(errTitle, errMessage);

      }

    }

    const body: {
      subType: string,
      bookVersionId: string,
      shipping: ShippingInfoModel
    } = {
      subType: this.subscriptionType,
      bookVersionId: this.bookVersionId,
      shipping: this.shippingInfo
    };

    this.cbInstance.openCheckout({
      hostedPage: () => {
        return this.cbSv.newSubscriptionCheckout(body);
      },
      loaded: () => {

      },
      error: (err) => {

        this.ngZone.run(() => {

          if (err.name === 'resource_already_exists') {

            const title = this.tSv.instant('reusable.error');
            const message = this.tSv.instant('billingInformation.subscriptionExistsMessage');

            this.modalSv.showAlertModal(title, message).subscribe(() => {
              this.router.navigate(['platform', 'dash']);
            });

            this.cbSv.updateStudentSubscriptionStatus().subscribe((student) => {

              if (student) {
                StudentModel.setCurrent(student);
              }

            });

          }

        });

      },
      close: () => {
        console.log('closed');
      },
      success: (hostedPageId) => {
        this.ngZone.run(() => {

          // TODO: add the create the dropp order.
          if (this.subPricing && this.subPricing.includesBook && this.bookVersion && CountryModel.getHomepageCountry() === CountryModel.ISL) {
            this.droppSv.createDroppOrder(hostedPageId, this.bookVersion.name, this.shippingInfo).subscribe((success) => {
            });
          }

          // Navigate to success page.
          this.cbSv.updateStudentSubscriptionStatus().subscribe((student) => {
            if (student) {
              StudentModel.setCurrent(student);
            }
          });

          this.router.navigate(['thankpurchase'], { queryParams: { subscription: this.subscriptionType } });

        });
      },
      step: (value) => {

      }
    });

  }

  onStartTrialClicked() {

    this.cbSv.startFreeTrial().subscribe((student) => {
      StudentModel.setCurrent(student);
      this.router.navigate(['thankpurchase'], { queryParams: { subscription: this.subscriptionType } });
    }, (e) => {
      this.modalSv.showErrorModal(this.tSv.instant('reusable.error'), e.message);
    });

  }

  getSubscriptionTitle(): string {

    if (this.subscriptionType === 'trial') {
      return 'lp2.pricing.Subscriptions.freeTrial.title';
    } else if (this.subscriptionType === PricingModel.SubscriptionTypes['6M']) {
      return 'lp2.pricing.Subscriptions.6m.title';
    } else if (this.subscriptionType === PricingModel.SubscriptionTypes['3M']) {
      return 'lp2.pricing.Subscriptions.3m.title';
    } else if (this.subscriptionType === PricingModel.SubscriptionTypes['1M']) {
      return 'lp2.pricing.Subscriptions.1m.title';
    }

    return 'lp2.pricing.Subscriptions.12m.title';
  }

  bookVersionImgURL(): string {

    if (this.bookVersion) {

      return this.bookVersion.bookVersionImgURL();
    }

    return null;
  }

  onTCsClicked() {
    this.router.navigate([UserModel.getCurrent().culture, 'tc']);
  }

}
